// Service to handle AI draft school files
export interface AIDraftSchool {
  id: string;
  name: string;
  data: any;
  fileName: string;
}

export class AIDraftService {
  // List of all AI draft schools in the schoolsToBeReviewed folder
  private static readonly AI_DRAFT_SCHOOLS: string[] = [
    // All original US schools have been processed and moved to schoolsApprovedAndAdded
    // This array is now empty as all 65 universities have been completed
  ];

  static async getAIDraftSchools(): Promise<AIDraftSchool[]> {
    // Sample data from actual university files for demonstration
    // In a real implementation, this would read from the schoolsToBeReviewed folder
    const sampleUniversityData = {
      // All US schools have been processed and moved to schoolsApprovedAndAdded
      // This object is now empty as all 65 universities have been completed
    };

    const aiDraftSchools: AIDraftSchool[] = [];

    for (const fileName of this.AI_DRAFT_SCHOOLS) {
      // Extract school name from filename
      const schoolName = fileName
        .replace('-data.json', '')
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      // Use sample data if available, otherwise create basic structure
      const schoolData = sampleUniversityData[fileName as keyof typeof sampleUniversityData] || {
        "AthleticsNickname": "",
        "AthleticsWebsite": "",
        "SchoolAdmissionsMin": "",
        "SchoolBasicRequirements": "",
        "SchoolCatalog": "",
        "SchoolCity": "",
        "SchoolDescription": "",
        "SchoolFTUGCount": "0",
        "SchoolInstagramURL": "",
        "SchoolLocationLatitude": "",
        "SchoolLocationLongitude": "",
        "SchoolName": schoolName,
        "SchoolPhone": "",
        "SchoolProfileImageURL": "",
        "SchoolProvinceState": "",
        "SchoolResultsImageURL": "",
        "SchoolRoomAndBoardHigh": "0",
        "SchoolRoomAndBoardLow": "0",
        "SchoolTwitterURL": "",
        "SchoolType": "University",
        "SchoolWebsite": "",
        "SchoolWikiURL": "",
        "SchoolYoutubeURL": "",
        "acceptanceRate": "0",
        "lastUpdated": Date.now().toString()
      };

      aiDraftSchools.push({
        id: fileName.replace('.json', ''),
        name: schoolName,
        data: schoolData,
        fileName: fileName
      });
    }

    return aiDraftSchools;
  }

  // Convert AI draft data to form data format
  static convertAIDraftToFormData(draftData: any): any {
    return {
      name: draftData.SchoolName || '',
      type: draftData.SchoolType || 'University',
      city: draftData.SchoolCity || '',
      province: draftData.SchoolProvinceState || '',
      website: draftData.SchoolWebsite || '',
      image: draftData.SchoolProfileImageURL || '',
      description: draftData.SchoolDescription || '',
      acceptanceRate: parseFloat(draftData.acceptanceRate) || 0,
      enrollment: parseInt(draftData.SchoolFTUGCount) || 0,
      roomAndBoardLow: parseFloat(draftData.SchoolRoomAndBoardLow) || 0,
      roomAndBoardHigh: parseFloat(draftData.SchoolRoomAndBoardHigh) || 0,
      resultsImage: draftData.SchoolResultsImageURL || '',
      feedIcon: '', // Not in AI draft
      athleticsWebsite: draftData.AthleticsWebsite || '',
      admissionsMinimum: draftData.SchoolAdmissionsMin || '',
      athleticsNickname: draftData.AthleticsNickname || '',
      primaryLanguage: '', // Not in AI draft
      SchoolPhone: draftData.SchoolPhone || '',
      SchoolTwitterURL: draftData.SchoolTwitterURL || '',
      SchoolInstagramURL: draftData.SchoolInstagramURL || '',
      SchoolWikiURL: draftData.SchoolWikiURL || '',
      SchoolYoutubeURL: draftData.SchoolYoutubeURL || '',
      SchoolResultsImageURL: draftData.SchoolResultsImageURL || '',
      SchoolLocationLatitude: draftData.SchoolLocationLatitude || '',
      SchoolLocationLongitude: draftData.SchoolLocationLongitude || '',
      SchoolAdmissionsACT: '', // Not in AI draft - will be empty for user to fill
      SchoolAdmissionsGPA: '', // Not in AI draft - will be empty for user to fill
      SchoolAdmissionsSAT: '', // Not in AI draft - will be empty for user to fill
      programs: [],
      athletics: [],
      events: [],
      scholarships: [],
      admissionRequirements: draftData.SchoolBasicRequirements || '',
      socialMedia: []
    };
  }

  // Move file from schoolsToBeReviewed to schoolsApprovedAndAdded
  static async moveFileToApproved(fileName: string): Promise<void> {
    // In a real implementation, this would move the file
    // For now, we'll just log the action
    console.log(`Moving file ${fileName} from schoolsToBeReviewed to schoolsApprovedAndAdded`);
    
    // In a real app, you might want to:
    // 1. Read the file from schoolsToBeReviewed folder
    // 2. Write it to schoolsApprovedAndAdded folder
    // 3. Delete the original file
    // This would typically be done via a backend API call
  }
}
